import * as React from 'react';
import {
  validateNotEmpty,
  validateEmail,
  validatePhoneNumber,
  validateIsOnlyNumbers,
  validateOrgNum,
} from '../validation';
import { Collapse } from 'react-collapse';
import { IValidation, IAddress, IFormConfig } from '../types';
import ValidationFeeeback from './ValidationFeedback';
import AvailabilityFeedback from './AvailabilityFeedback';

interface IProps {
  availability: {
    code: number | null;
    partnerId: string;
  };
  target: IAddress;
  multipleLocations: boolean;
  brlLocation: boolean;
  returnUrl: (url?: string) => string;
  tryAgain: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
}

interface IState {
  company: string;
  Org_nummer__c: string;
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  Number_of_Access_Points__c: string;
  Title: string;
  description: string;
  wifiOffer: boolean;
  validations: { [index: string]: any };
  displayDescription: boolean;
  showSubmit: boolean;
  [companey: string]: any;
}

export default class RequestForm extends React.Component<IProps, IState> {
  leadRecordTypes: { singleLocation: string; multipleLocations: string; brlLocation: string } = {
    singleLocation: 'Bedrift',
    multipleLocations: 'Nasjonal kunde',
    brlLocation: 'Boligselskap',
  };

  formConfig: IFormConfig = {
    actionUrl: 'https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8',
    oid: '00D24000000Ydt3',
    retUrl: this.props.returnUrl(),
    leadSource: 'Leads Generator',
    debugMode: '0',
    debugEmail: 'frederick.rogers@altibox.no',
    salesForceId: '00N1p00000JoTzZ',
  };

  constructor(props: IProps) {
    super(props);

    this.state = {
      company: '',
      Org_nummer__c: '',
      first_name: '',
      last_name: '',
      email: '',
      mobile: '',
      Number_of_Access_Points__c: '',
      Title: '',
      description: '',
      validations: {},
      displayDescription: false,
      showSubmit: false,
      wifiOffer: false,
    };
  }

  insertIfValidationError = (validations: { [index: string]: IValidation }, validation: IValidation) => {
    validations[validation.fieldName] = validation;
  };

  doValidation = () => {
    let validations: {
      [index: string]: IValidation;
    } = {};
    this.insertIfValidationError(validations, validateNotEmpty(this.state.company, 'company'));
    this.insertIfValidationError(validations, validateNotEmpty(this.state.first_name, 'first_name'));
    this.insertIfValidationError(validations, validateNotEmpty(this.state.last_name, 'last_name'));
    this.insertIfValidationError(validations, validateEmail(this.state.email, 'email'));
    this.insertIfValidationError(validations, validatePhoneNumber(this.state.mobile, 'mobile'));
    if (this.state.Org_nummer__c) {
      this.insertIfValidationError(validations, validateOrgNum(this.state.Org_nummer__c, 'Org_nummer__c'));
    }
    if (this.props.brlLocation) {
      this.insertIfValidationError(
        validations,
        validateIsOnlyNumbers(this.state.Number_of_Access_Points__c, 'Number_of_Access_Points__c', true)
      );
      this.insertIfValidationError(validations, validateNotEmpty(this.state.Title, 'Title'));
    }
    this.setState({ validations });
    const isFormValid = !Object.values(validations).some((value) => value.hasError);
    this.setState({ showSubmit: isFormValid });
    return isFormValid;
  };

  componentDidMount = () => {
    this.doValidation();
  };

  handleFormSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    this.doValidation();
  };

  handleToggleDescription = () => {
    this.setState({ displayDescription: !this.state.displayDescription });
  };

  handleOnChange = (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.currentTarget;
    if (name === 'mobile' || name === 'Number_of_Access_Points__c' || name === 'Org_nummer__c') {
      validateIsOnlyNumbers(value, name, false).hasError || this.setState({ [name]: value }, this.doValidation);
    } else if (name === '00N0800000KKtbR') {
      this.setState({ wifiOffer: !this.state.wifiOffer });
    } else {
      this.setState({ [name]: value }, this.doValidation);
    }
  };

  getLeadRecordType = (): string => {
    if (this.props.brlLocation) {
      return this.leadRecordTypes.brlLocation;
    } else if (this.props.multipleLocations) {
      return this.leadRecordTypes.multipleLocations;
    } else {
      return this.leadRecordTypes.singleLocation;
    }
  };

  renderBrlFields = () => {
    return (
      <div className="row">
        <div className="col-sm-8">
          <div className="form-group">
            <label htmlFor="Title">
              Rolle i styre
              {this.state.validations.Title && <ValidationFeeeback validation={this.state.validations.Title} />}
            </label>
            <input
              onChange={this.handleOnChange}
              id="Title"
              className="form-control"
              type="text"
              name="Title"
              value={this.state.Title}
            />
          </div>
        </div>
        <div className="col-sm-4">
          <div className="form-group">
            <label htmlFor="Number_of_Access_Points__c">
              Antall boenheter
              {this.state.validations.Number_of_Access_Points__c && (
                <ValidationFeeeback validation={this.state.validations.Number_of_Access_Points__c} />
              )}
            </label>
            <input
              onChange={this.handleOnChange}
              id="Number_of_Access_Points__c"
              className="form-control"
              type="string"
              name="Number_of_Access_Points__c"
              value={this.state.Number_of_Access_Points__c}
            />
          </div>
        </div>
      </div>
    );
  };

  renderSubmitButton = () => {
    return (
      <button
        type="submit"
        id="submit-lead"
        className={`button btn btn-primary btn-block tracking-id-${this.props.brlLocation ? 'brl' : 'enterprise'}`}>
        meld din interesse
        {this.state.showSubmit && <i className="icon fa fa-check" aria-hidden="true" />}
      </button>
    );
  };

  getCompanyNameLabel = ({ includePersonalisation }: { includePersonalisation: boolean }) => {
    const companyTitle = this.props.brlLocation ? 'boligselskapet' : 'bedriften';
    const personalisation = this.props.brlLocation ? ' ditt' : ' din';
    return `Navn på ${companyTitle}${includePersonalisation ? personalisation : ''}`;
  };

  render = () => {
    return (
      <React.Fragment>
        <div className="row">
          <div className="col-sm-12">
            {this.props.brlLocation ? (
              ''
            ) : (
              <AvailabilityFeedback
                availabilityCode={this.props.availability.code}
                multiplelocations={this.props.multipleLocations}
              />
            )}
            <form onSubmit={this.handleFormSubmit} id="request-form" action={this.formConfig.actionUrl} method="POST">
              <input type="hidden" name="oid" value={this.formConfig.oid} />
              <input type="hidden" name="retURL" value={this.formConfig.retUrl} />
              <input type="hidden" name="lead_source" value={this.formConfig.leadSource} />
              <input type="hidden" name="debug" value={this.formConfig.debugMode} />
              <input type="hidden" name="debugEmail" value={this.formConfig.debugEmail} />
              <input type="hidden" name="Lead_RecordType__c" value={this.getLeadRecordType()} />
              <input type="hidden" name="Address_Id__c" value={this.props.target.id} />
              <input type="hidden" name="Subcontractor_Name_Geodata__c" value={this.props.availability.partnerId} />

              <div className="row">
                <div className="col-sm-8">
                  <div className="form-group">
                    <label htmlFor="company">
                      {this.getCompanyNameLabel({ includePersonalisation: false })}
                      {this.state.validations.company && (
                        <ValidationFeeeback validation={this.state.validations.company} />
                      )}
                    </label>
                    <input
                      onChange={this.handleOnChange}
                      id="company"
                      className="form-control"
                      type="text"
                      name="company"
                      placeholder={this.getCompanyNameLabel({ includePersonalisation: true })}
                      value={this.state.company}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="form-group">
                    <label htmlFor="Org_nummer__c">
                      Org. nummer{' '}
                      {this.state.validations.Org_nummer__c && (
                        <ValidationFeeeback validation={this.state.validations.Org_nummer__c} />
                      )}
                    </label>
                    <input
                      onChange={this.handleOnChange}
                      id="Org_nummer__c"
                      className="form-control"
                      type="text"
                      name="Org_nummer__c"
                      value={this.state.Org_nummer__c}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-sm-8">
                  <div className="form-group">
                    <label htmlFor="street">Adresse</label>
                    <input
                      id="street"
                      className="form-control"
                      type="text"
                      name="street"
                      value={`${this.props.target.street} ${this.props.target.num}`}
                      readOnly
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="form-group">
                    <label htmlFor="zip">Postnummer</label>
                    <input
                      id="zip"
                      className="form-control"
                      type="text"
                      name="zip"
                      value={this.props.target.zip}
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-sm-4">
                  <div className="form-group">
                    <label htmlFor="first_name">
                      Fornavn
                      {this.state.validations.first_name && (
                        <ValidationFeeeback validation={this.state.validations.first_name} />
                      )}
                    </label>
                    <input
                      onChange={this.handleOnChange}
                      id="first_name"
                      className="form-control"
                      type="text"
                      name="first_name"
                      value={this.state.first_name}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="form-group">
                    <label htmlFor="last_name">
                      Etternavn
                      {this.state.validations.last_name && (
                        <ValidationFeeeback validation={this.state.validations.last_name} />
                      )}
                    </label>
                    <input
                      onChange={this.handleOnChange}
                      id="last_name"
                      className="form-control"
                      type="text"
                      name="last_name"
                      value={this.state.last_name}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="form-group">
                    <label htmlFor="city">Poststed</label>
                    <input
                      id="city"
                      className="form-control"
                      type="text"
                      name="city"
                      value={this.props.target.city}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {this.props.brlLocation && this.renderBrlFields()}

              <div className="row">
                <div className="col-sm-8">
                  <div className="form-group">
                    <label htmlFor="email">
                      E-post
                      {this.state.validations.email && <ValidationFeeeback validation={this.state.validations.email} />}
                    </label>
                    <input
                      onChange={this.handleOnChange}
                      id="email"
                      className="form-control"
                      type="email"
                      name="email"
                      value={this.state.email}
                    />
                  </div>
                </div>
                <div className="col-sm-4">
                  <div className="form-group">
                    <label htmlFor="mobile">
                      Mobiltelefon
                      {this.state.validations.mobile && (
                        <ValidationFeeeback validation={this.state.validations.mobile} />
                      )}
                    </label>
                    <input
                      onChange={this.handleOnChange}
                      id="mobile"
                      className="form-control"
                      type="tel"
                      name="mobile"
                      value={this.state.mobile}
                    />
                  </div>
                </div>
              </div>
              {!this.props.brlLocation && (
                <div className="row">
                  <div className="col-sm-12">
                    <div className="form-group">
                      <div className="wifi checkbox">
                        <input
                          onChange={this.handleOnChange}
                          id="wifi"
                          className="form-control"
                          type="checkbox"
                          name="00N0800000KKtbR"
                          checked={this.state.wifiOffer}
                          value={this.state.wifiOffer ? '1' : '0'}
                        />
                        <label htmlFor="wifi">Jeg ønsker WiFi fra Altibox Bedrift</label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-sm-12">
                  <div className="form-group">
                    <label onClick={this.handleToggleDescription} htmlFor="description">
                      <span>
                        <i
                          className={`fa fa-caret-${this.state.displayDescription ? 'down' : 'right'} fa-lg`}
                          aria-hidden="true"
                        />
                        {' Legg til personlig melding'}
                      </span>
                    </label>
                    <Collapse isOpened={this.state.displayDescription}>
                      <textarea
                        onChange={this.handleOnChange}
                        id={this.formConfig.salesForceId}
                        className="form-control"
                        name={this.formConfig.salesForceId}
                        rows={3}
                        wrap="soft"
                      />
                    </Collapse>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-sm-12">
                  <div className="form-group">{this.state.showSubmit ? this.renderSubmitButton() : ''}</div>
                </div>
              </div>
            </form>

            <div className="information">
              <div className="row">
                <div className="col-sm-12">
                  <p>
                    Vi oppbevarer informasjonen om deg for å vurdere ytterligere utbygging av bredbånd der du bor. Denne
                    informasjonen vil vi slette dersom du informerer oss om at du ikke lenger er interessert.
                  </p>

                  <button id="wp-" className="try-again" type="button" onClick={this.props.tryAgain}>
                    Valgt feil adresse?
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  };
}
